import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { contactDetails } from "@/mocks/products";

type LegalSlug = "agb" | "datenschutz" | "impressum";

type LegalContent = {
  title: string;
  intro: string;
  sections: { heading: string; paragraphs: string[] }[];
};

const legalContent: Record<LegalSlug, LegalContent> = {
  agb: {
    title: "Allgemeine Geschäftsbedingungen",
    intro:
      "Für alle Lieferungen und Leistungen der Franz Drexelius Metallverarbeitung GmbH gelten die nachfolgenden Bedingungen.",
    sections: [
      {
        heading: "1. Geltungsbereich",
        paragraphs: [
          "Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Angebote, Lieferungen und Leistungen der Franz Drexelius Metallverarbeitung GmbH gegenüber Unternehmern, juristischen Personen des öffentlichen Rechts und öffentlich-rechtlichen Sondervermögen.",
          "Abweichende Einkaufsbedingungen des Bestellers werden nur anerkannt, wenn ihnen ausdrücklich schriftlich zugestimmt wurde.",
        ],
      },
      {
        heading: "2. Angebot und Vertragsschluss",
        paragraphs: [
          "Unsere Angebote sind freibleibend und unverbindlich, sofern sie nicht ausdrücklich als verbindlich bezeichnet werden.",
          "Ein Vertrag kommt erst mit unserer schriftlichen Auftragsbestätigung oder mit der Auslieferung der Ware zustande.",
        ],
      },
      {
        heading: "3. Preise und Zahlung",
        paragraphs: [
          "Alle Preise verstehen sich, sofern nicht anders angegeben, ab Werk in Euro zuzüglich der gesetzlichen Umsatzsteuer.",
          "Sonderausführungen und Fertigungslose ab 300 Stück werden individuell kalkuliert. Zahlungsbedingungen werden in der Auftragsbestätigung geregelt.",
        ],
      },
      {
        heading: "4. Lieferung und Lieferzeit",
        paragraphs: [
          "Lieferzeiten sind Richtwerte. Wir bemühen uns um termingerechte Auslieferung; verbindliche Termine bedürfen der schriftlichen Bestätigung.",
          "Verkaufsverpackung, Versandart und Versandweg wählen wir nach bestem Ermessen, sofern keine besonderen Vereinbarungen getroffen wurden.",
        ],
      },
      {
        heading: "5. Gewährleistung",
        paragraphs: [
          "Die gelieferten Teile sind unverzüglich nach Erhalt zu prüfen. Erkennbare Mängel sind unverzüglich schriftlich anzuzeigen.",
          "Bei berechtigten Mängelrügen leisten wir nach unserer Wahl Nachbesserung oder Ersatzlieferung. Weitergehende Ansprüche richten sich nach den gesetzlichen Vorschriften.",
        ],
      },
      {
        heading: "6. Eigentumsvorbehalt",
        paragraphs: [
          "Die gelieferte Ware bleibt bis zur vollständigen Bezahlung aller Forderungen aus der Geschäftsverbindung unser Eigentum.",
        ],
      },
      {
        heading: "7. Schlussbestimmungen",
        paragraphs: [
          "Es gilt deutsches Recht. Gerichtsstand und Erfüllungsort ist, soweit gesetzlich zulässig, unser Firmensitz.",
          "Sollten einzelne Bestimmungen dieser Bedingungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen davon unberührt.",
        ],
      },
    ],
  },
  datenschutz: {
    title: "Datenschutzerklärung",
    intro:
      "Der Schutz Ihrer personenbezogenen Daten ist uns wichtig. Nachfolgend informieren wir Sie darüber, welche Daten wir erheben und wie wir sie verwenden.",
    sections: [
      {
        heading: "1. Verantwortlicher",
        paragraphs: [
          `Verantwortlich für die Datenverarbeitung auf dieser Website ist die ${contactDetails.company}, ${contactDetails.location}. Sie erreichen uns telefonisch unter ${contactDetails.phone} oder über unser Kontaktformular.`,
        ],
      },
      {
        heading: "2. Erhebung und Verwendung von Daten",
        paragraphs: [
          "Beim Aufruf unserer Website werden automatisch technische Zugriffsdaten verarbeitet, die Ihr Browser übermittelt. Diese Daten dienen ausschließlich dem technischen Betrieb und der Sicherheit der Website.",
          "Wenn Sie uns über das Kontaktformular eine Anfrage senden, verarbeiten wir die von Ihnen angegebenen Daten (z. B. Firma, Name, E-Mail-Adresse, Telefonnummer und Nachricht) ausschließlich zur Bearbeitung Ihrer Anfrage und zur Anbahnung oder Erfüllung eines Vertrags.",
        ],
      },
      {
        heading: "3. Rechtsgrundlage",
        paragraphs: [
          "Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung bzw. Vertragsanbahnung) sowie Art. 6 Abs. 1 lit. f DSGVO für das berechtigte Interesse an einem sicheren Betrieb der Website.",
        ],
      },
      {
        heading: "4. Weitergabe von Daten",
        paragraphs: [
          "Eine Weitergabe Ihrer Daten an Dritte erfolgt nicht, es sei denn, dies ist zur Erfüllung unserer Leistungen erforderlich oder gesetzlich vorgeschrieben.",
        ],
      },
      {
        heading: "5. Speicherdauer",
        paragraphs: [
          "Wir speichern Ihre Daten nur so lange, wie es für den jeweiligen Zweck erforderlich ist oder gesetzliche Aufbewahrungsfristen dies vorschreiben.",
        ],
      },
      {
        heading: "6. Ihre Rechte",
        paragraphs: [
          "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen die Verarbeitung Ihrer Daten.",
          "Zudem steht Ihnen ein Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde zu.",
        ],
      },
    ],
  },
  impressum: {
    title: "Impressum",
    intro: "Angaben gemäß § 5 TMG und § 55 RStV.",
    sections: [
      {
        heading: "Anbieter und Anschrift",
        paragraphs: [
          `${contactDetails.company}`,
          `${contactDetails.street}`,
          `${contactDetails.city}, Deutschland`,
          `Postanschrift: ${contactDetails.postOffice}, ${contactDetails.postalCode}`,
        ],
      },
      {
        heading: "Kontakt",
        paragraphs: [
          "Telefon: +49 2721 7237",
          "Telefax: +49 2721 6942",
          `E-Mail: ${contactDetails.email}`,
        ],
      },
      {
        heading: "Vertretungsberechtigt",
        paragraphs: [`Geschäftsführer: ${contactDetails.director}`],
      },
      {
        heading: "Registereintrag",
        paragraphs: [
          `Handelsregister: ${contactDetails.register}`,
          `Registergericht: ${contactDetails.registerCourt}`,
        ],
      },
      {
        heading: "Umsatzsteuer-Identifikationsnummer",
        paragraphs: [
          `Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz: ${contactDetails.vatId}`,
        ],
      },
      {
        heading: "EU-Streitschlichtung",
        paragraphs: [
          "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/",
          `Unsere E-Mail-Adresse finden Sie oben im Impressum: ${contactDetails.email}`,
        ],
      },
      {
        heading: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
        paragraphs: [
          "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
        ],
      },
      {
        heading: "Verantwortlich für den Inhalt",
        paragraphs: [
          `Verantwortlich für journalistisch-redaktionelle Inhalte gemäß § 55 Abs. 2 RStV ist die Geschäftsführung (${contactDetails.director}) der ${contactDetails.company}, ${contactDetails.city}.`,
        ],
      },
      {
        heading: "Haftung für Inhalte und Links",
        paragraphs: [
          "Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Für die Inhalte externer Links sind ausschließlich deren Betreiber verantwortlich.",
        ],
      },
    ],
  },
};

function LegalPage({ slug }: { slug: LegalSlug }) {
  const content = legalContent[slug];

  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200">
        <nav aria-label="Brotkrumen" className="w-full px-4 md:px-8 py-4">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Start
              </Link>
            </li>
            <li aria-hidden="true">
              <i className="ri-arrow-right-s-line" />
            </li>
            <li className="text-foreground-800">{content.title}</li>
          </ol>
        </nav>
      </div>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-14 md:py-20 max-w-3xl">
          <h1 className="font-heading font-semibold text-3xl md:text-4xl text-foreground-950">
            {content.title}
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
            {content.intro}
          </p>

          <div className="mt-10 flex flex-col gap-8">
            {content.sections.map((section) => (
              <article key={section.heading}>
                <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-950">
                  {section.heading}
                </h2>
                <div className="mt-3 flex flex-col gap-2">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <Link
            to="/kontakt"
            className="mt-12 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-600 text-background-50 font-label text-sm uppercase tracking-wide whitespace-nowrap hover:bg-primary-500 transition-colors"
          >
            <i className="ri-mail-send-line text-base" aria-hidden="true" />
            Zum Kontaktformular
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}

export default LegalPage;